"use client";

import type { MouseEvent, ReactElement, ReactNode } from "react";

import {
  ICON_BUTTON_FOCUS_CLASS,
  ICON_BUTTON_HIT_AREA_CLASS,
  ICON_BUTTON_SIZES,
  type FetchIconButtonSize,
} from "./_logic/button.constants";
import { FetchButton } from "./FetchButton";

/** How an icon button is drawn. `danger` is a ghost in the destructive color. */
export type FetchIconButtonVariant = "ghost" | "outline" | "danger";

interface FetchIconButtonProps {
  /** The glyph, usually a Lucide icon element. Sized by the button; no class needed. */
  icon: ReactNode;
  /** Required. Becomes both the tooltip text and the accessible name. */
  label: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** xs 24px, sm 32px, md 36px visual box. Defaults to "sm". */
  size?: FetchIconButtonSize;
  /** Defaults to "ghost". */
  variant?: FetchIconButtonVariant;
  /** Keyboard shortcut shown beside the label in the tooltip, for example "⌘K". */
  shortcut?: string;
  disabled?: boolean;
  /** Why the button is disabled. Shown in the tooltip instead of the label; see `FetchButton`. */
  disabledReason?: string;
  loading?: boolean;
  type?: "button" | "submit" | "reset";
  /** Renders a link with the same look. */
  href?: string;
  target?: string;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  ariaPressed?: boolean;
  ariaExpanded?: boolean;
  ariaControls?: string;
  /** -1 takes the button out of the tab order when a keyboard shortcut reaches it instead. */
  tabIndex?: number;
  className?: string;
}

/**
 * The tooltip body: the label, plus the shortcut in a quiet key cap when there is one.
 *
 * @param label - The button's label.
 * @param shortcut - Optional shortcut text.
 * @returns The label string, or the label and key cap.
 * @throws Never.
 */
function tooltipBody(label: string, shortcut: string | undefined): ReactNode {
  if (!shortcut) return label;
  return (
    <span className="flex items-center gap-2">
      {label}
      <kbd className="rounded bg-white/15 px-1 font-sans text-xs font-medium leading-4 text-gray-200">{shortcut}</kbd>
    </span>
  );
}

/**
 * Icon-only button. `label` is required, so every icon control has both a tooltip and an
 * accessible name.
 *
 * Built on `FetchButton` (its neutral ghost, neutral outline and destructive ghost colors) and
 * `FetchTooltip`. Adds a fixed square box per size, a hit area of at least 32px (40px on a touch
 * screen) that does not change layout, and an inset focus ring that an `overflow: hidden` parent
 * cannot clip.
 *
 * @param props - See {@link FetchIconButtonProps}.
 * @returns The icon button wrapped in its tooltip.
 * @throws Never.
 */
export function FetchIconButton({
  icon,
  label,
  onClick,
  size = "sm",
  variant = "ghost",
  shortcut,
  disabled = false,
  disabledReason,
  loading = false,
  type = "button",
  href,
  target,
  tooltipSide = "top",
  ariaPressed,
  ariaExpanded,
  ariaControls,
  tabIndex,
  className = "",
}: FetchIconButtonProps): ReactElement {
  // Derived values
  const sizing = ICON_BUTTON_SIZES[size];

  // Render
  return (
    <FetchButton
      iconOnly
      leftIcon={icon}
      onClick={onClick}
      size={sizing.buttonSize}
      variant={variant === "outline" ? "outline" : "ghost"}
      color={variant === "danger" ? "destructive" : "neutral"}
      disabled={disabled}
      disabledReason={disabledReason}
      loading={loading}
      type={type}
      href={href}
      target={target}
      tooltip={tooltipBody(label, shortcut)}
      tooltipSide={tooltipSide}
      aria-label={label}
      ariaPressed={ariaPressed}
      ariaExpanded={ariaExpanded}
      ariaControls={ariaControls}
      tabIndex={tabIndex}
      className={`${sizing.box} ${sizing.glyph} ${ICON_BUTTON_HIT_AREA_CLASS} ${ICON_BUTTON_FOCUS_CLASS} ${className}`}
    />
  );
}
